import { FIT_DEFAULT } from '@src/config/videoSettings';
import { FitType } from '@src/editor/models/layout/legacyLayoutMapping';
import { useUserIdentity } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import ChevronDownIcon from '@src/svgs/ChevronDownIcon';
import ContainIcon from '@src/svgs/fit/ContainIcon';
import CoverIcon from '@src/svgs/fit/CoverIcon';
import FillIcon from '@src/svgs/fit/FillIcon';
import SplitPresentationIcon from '@src/svgs/fit/SplitPresentation';
import SplitTwoIcon from '@src/svgs/fit/SplitTwoIcon';
import SplitTwoVerticalIcon from '@src/svgs/fit/SplitTwoVerticalIcon';
import { findOriginalVideoElementAtCurrentTime } from '@src/utility/elements';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';

import { AspectRatio } from '../../types.ts/video';
import { COMPACT_TOOLTIP_STYLE } from '../common/tooltipStyles';
import useLayoutAwareFitPropertyState from '../sidepanel/useLayoutAwareFitPropertyState';
import { useOutsideAlerter } from '../transcript/useClickOutside';

import type { BaseElement } from '../sidepanel/CompositionSettings/types';
import type { ElementState } from '@src/renderer/ElementState';
import type { JSX } from 'react';

const getIconColor = (isSelected: boolean) =>
  isSelected ? 'var(--accent)' : 'var(--text-secondary)';

type IconProps = { isSelected: boolean };

type FitOptionBasic = {
  caption: string;
  captionShort?: string;
  value: string;
  icon: (props: IconProps) => JSX.Element;
};

type FitSubOption = Omit<FitOptionBasic, 'icon'>;

type FitOption = FitOptionBasic & {
  subOptions?: {
    items: FitSubOption[];
    showForAspectRatios: AspectRatio[];
  };
};

const containSubOptions: FitSubOption[] = [
  {
    caption: 'Blurred Background',
    captionShort: 'Blurred',
    value: FitType.BlurredBackground,
  },
  {
    caption: 'Black Background',
    captionShort: 'Black',
    value: FitType.Contain, // Regular contain with black background
  },
];

const mainOptions: FitOption[] = [
  {
    caption: 'Cover',
    value: FitType.Cover,
    icon: (props: IconProps) => (
      <CoverIcon strokeColor={getIconColor(props.isSelected)} />
    ),
  },
  {
    caption: 'Contain',
    value: FitType.Contain,
    icon: (props: IconProps) => (
      <ContainIcon strokeColor={getIconColor(props.isSelected)} />
    ),
    subOptions: {
      items: containSubOptions,
      showForAspectRatios: [AspectRatio.AR_9_16],
    },
  },
];

const fillOption: FitOption = {
  caption: 'Fill',
  value: FitType.Fill,
  icon: () => <FillIcon />,
};

const stackedLayoutOptions: FitOption[] = [
  {
    caption: 'Stacked 1:1',
    value: FitType.Stacked1To1,
    icon: (props: IconProps) => (
      <SplitTwoIcon strokeColor={getIconColor(props.isSelected)} />
    ),
  },
  {
    caption: 'Stacked 1:3',
    value: FitType.Stacked1To3,
    icon: (props: IconProps) => (
      <SplitPresentationIcon strokeColor={getIconColor(props.isSelected)} />
    ),
  },
];

const splitVerticalOption: FitOption = {
  caption: 'Split Vertical 1:1',
  value: FitType.SplitVertical,
  icon: (props: IconProps) => (
    <SplitTwoVerticalIcon strokeColor={getIconColor(props.isSelected)} />
  ),
};

const FitDropdown = observer(() => {
  const videoCreator = useVideoCreatorStore();
  const selectedAspectRatio =
    videoCreator.currentVideo?.aspectRatio ||
    videoCreator.getDefaultAspectRatio();

  const userIdentity = useUserIdentity();
  const {
    enableStackedLayouts,
    creatorStudioTimelineEnableFit13,
    creatorStudioTimelineEnableFitFill,
    creatorStudioTimelineEnableFitSplitHorizontal11,
    creatorStudioTimelineEnableFitSplitHorizontal13,
    creatorStudioTimelineEnableFitSplitVertical11,
  } = useFlagsCombination(userIdentity.currentRole);
  const [activeElement, setActiveElement] = useState<BaseElement | null>(null);
  const [activeElementState, setActiveElementState] =
    useState<ElementState | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const [subDropdownOpen, setSubDropdownOpen] = useState<boolean>(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter(dropdownRef, () => {
    setDropdownOpen(false);
    setSubDropdownOpen(false);
  });

  const currentFit = activeElement?.fit || FIT_DEFAULT;

  useEffect(() => {
    const baseElement = findOriginalVideoElementAtCurrentTime(videoCreator);
    setActiveElement(baseElement);

    if (!baseElement) {
      setActiveElementState(null);
      return;
    }

    const { sceneLayoutIndexStore, editorStore } = videoCreator.rootStore;
    let elementState = editorStore.getElementStateById(baseElement.id);

    if (!elementState) {
      const representationId =
        sceneLayoutIndexStore.resolveVisibleRepresentationId(
          baseElement.id,
          videoCreator.time,
        );
      const parentId =
        representationId && representationId !== baseElement.id
          ? sceneLayoutIndexStore.resolveParentId(representationId)
          : undefined;
      if (parentId) {
        elementState = editorStore.getElementStateById(parentId);
      }
    }

    setActiveElementState(elementState ?? null);
  }, [videoCreator.time, videoCreator.state]);

  const options = useMemo(() => {
    const baseOptions = [
      ...mainOptions,
      ...(creatorStudioTimelineEnableFitFill ? [fillOption] : []),
      ...(creatorStudioTimelineEnableFitSplitVertical11
        ? [splitVerticalOption]
        : []),
    ];

    if (!enableStackedLayouts || selectedAspectRatio !== AspectRatio.AR_9_16) {
      return baseOptions;
    }
    const filteredStackedOptions = stackedLayoutOptions.filter(option => {
      if (option.value === FitType.Stacked1To1) {
        return creatorStudioTimelineEnableFitSplitHorizontal11;
      }
      if (option.value === FitType.Stacked1To3) {
        return (
          creatorStudioTimelineEnableFit13 &&
          creatorStudioTimelineEnableFitSplitHorizontal13
        );
      }
      return true;
    });
    return [...baseOptions, ...filteredStackedOptions];
  }, [
    enableStackedLayouts,
    selectedAspectRatio,
    creatorStudioTimelineEnableFit13,
    creatorStudioTimelineEnableFitFill,
    creatorStudioTimelineEnableFitSplitHorizontal11,
    creatorStudioTimelineEnableFitSplitHorizontal13,
    creatorStudioTimelineEnableFitSplitVertical11,
  ]);

  const { determineValueForStackedLayout, handleStackedLayoutFitChange } =
    useLayoutAwareFitPropertyState({
      options,
      propertyName: 'fit',
      defaultValue: FIT_DEFAULT,
      activeElement: activeElementState!,
    });

  const displayedFit = useMemo(() => {
    if (!activeElementState) {
      return currentFit;
    }

    const mappedValue = determineValueForStackedLayout(
      activeElementState.source.id,
      currentFit,
    );
    if (mappedValue) {
      return mappedValue;
    }

    return currentFit;
  }, [
    activeElementState,
    currentFit,
    determineValueForStackedLayout,
    videoCreator.time,
  ]);

  const renderedFitIcon = useMemo<JSX.Element>(() => {
    for (const option of options) {
      // Check if any sub-option matches
      if (
        option.subOptions?.showForAspectRatios.includes(selectedAspectRatio)
      ) {
        const matchingSubOption = option.subOptions.items.find(
          subOpt => subOpt.value === displayedFit,
        );
        if (matchingSubOption) {
          return option.icon({ isSelected: false });
        }
      }

      // Check if this option itself matches
      if (option.value === displayedFit) {
        return option.icon({ isSelected: false });
      }
    }

    switch (displayedFit) {
      case 'fill':
        return <FillIcon />;
      default:
        return <CoverIcon />;
    }
  }, [displayedFit, options, selectedAspectRatio]);

  const displayedCaption = useMemo(() => {
    for (const option of options) {
      // Check if any sub-option matches
      if (
        option.subOptions?.showForAspectRatios.includes(selectedAspectRatio)
      ) {
        const matchingSubOption = option.subOptions.items.find(
          subOpt => subOpt.value === displayedFit,
        );
        if (matchingSubOption) {
          return `${option.captionShort ?? option.caption}: ${
            matchingSubOption.captionShort ?? matchingSubOption.caption
          }`;
        }
      }

      // Check if this option itself matches
      if (option.value === displayedFit) {
        return option.caption;
      }
    }

    return displayedFit;
  }, [displayedFit, options, selectedAspectRatio]);

  return (
    <Main
      disabled={!videoCreator.isVideoCreatorReady || !activeElementState}
      ref={dropdownRef}
    >
      <FitItem
        onClick={() =>
          videoCreator.isVideoCreatorReady &&
          activeElementState &&
          setDropdownOpen(!dropdownOpen)
        }
        data-tooltip-id="fit-disabled-tooltip"
      >
        <FitItemContent>
          <FitRatioLogo>{renderedFitIcon}</FitRatioLogo>
          <SelectedFit>{displayedCaption}</SelectedFit>
        </FitItemContent>
        <DropdownIcon isOpen={dropdownOpen}>
          <ChevronDownIcon strokeColor="currentColor" />
        </DropdownIcon>
      </FitItem>
      {(!videoCreator.isVideoCreatorReady || !activeElementState) &&
        ReactDOM.createPortal(
          <Tooltip
            id="fit-disabled-tooltip"
            content="Select a video element on the timeline"
            style={COMPACT_TOOLTIP_STYLE}
            place="bottom"
            positionStrategy="fixed"
            noArrow
            opacity={1}
          />,
          document.body,
        )}
      {dropdownOpen && (
        <>
          <FitDropDown>
            {options.map(option => {
              const shouldShowSubOptions =
                !!option.subOptions?.showForAspectRatios.includes(
                  selectedAspectRatio,
                );

              // Parent should be selected if any of its sub-options are selected
              const isSubOptionSelected =
                shouldShowSubOptions &&
                !!option.subOptions?.items.some(
                  subOpt => subOpt.value === displayedFit,
                );
              const isSelected =
                displayedFit === option.value || isSubOptionSelected;

              const tooltipId = `fit-${option.value}-tooltip`;
              const FitIcon = option.icon;

              return (
                <FitRowWrapper key={option.value}>
                  <FitRow
                    isSelected={isSelected}
                    onClick={async () => {
                      if (shouldShowSubOptions) {
                        setSubDropdownOpen(!subDropdownOpen);
                      } else {
                        if (activeElementState) {
                          setDropdownOpen(false);
                          setSubDropdownOpen(false);
                          await handleStackedLayoutFitChange(option.value);
                        }
                      }
                    }}
                    onMouseEnter={() => {
                      if (shouldShowSubOptions) {
                        setSubDropdownOpen(true);
                      } else {
                        setSubDropdownOpen(false);
                      }
                    }}
                  >
                    <FitRatioLogo>
                      <FitIcon isSelected={isSelected} />
                    </FitRatioLogo>
                    {`${option.caption}`}
                    <Tooltip
                      id={tooltipId}
                      style={COMPACT_TOOLTIP_STYLE}
                      place="right"
                      positionStrategy="fixed"
                      noArrow
                    />
                  </FitRow>
                  {shouldShowSubOptions && subDropdownOpen && (
                    <SubDropDown>
                      {option.subOptions?.items.map(subOption => {
                        const isSubSelected = displayedFit === subOption.value;
                        return (
                          <FitRow
                            key={subOption.value}
                            isSelected={isSubSelected}
                            onClick={async () => {
                              if (activeElementState) {
                                setDropdownOpen(false);
                                setSubDropdownOpen(false);
                                await handleStackedLayoutFitChange(
                                  subOption.value,
                                );
                              }
                            }}
                          >
                            {`${subOption.caption}`}
                          </FitRow>
                        );
                      })}
                    </SubDropDown>
                  )}
                </FitRowWrapper>
              );
            })}
          </FitDropDown>
        </>
      )}
    </Main>
  );
});

export default FitDropdown;

const Main = styled.div<{ disabled: boolean }>`
  position: relative;
  z-index: 10000;
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  cursor: ${props => (props.disabled ? 'wait' : 'pointer')};
`;

const DropdownIcon = styled.div<{ isOpen: boolean }>`
  margin-left: auto;
  display: flex;
  align-items: center;
  color: var(--accent);
  transition: transform var(--transition-fast);
  transform: rotate(${props => (props.isOpen ? '180deg' : '0deg')});
`;

const SelectedFit = styled.div`
  text-transform: capitalize;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: 100%;
  transition: color var(--transition-fast);
`;

const FitRow = styled.div<{ isSelected: boolean }>`
  position: relative;
  display: flex;
  box-sizing: border-box;
  text-transform: capitalize;
  align-items: center;
  min-height: 32px;
  font-size: var(--text-xs);
  color: ${({ isSelected }) =>
    isSelected ? 'var(--accent)' : 'var(--text-secondary)'};
  padding: 0 10px;
  gap: 8px;
  border-radius: 6px;
  user-select: none;

  &:hover {
    background-color: var(--surface-elevated);
    color: ${({ isSelected }) =>
      isSelected ? 'var(--accent)' : 'var(--foreground)'};
  }
`;

const FitRatioLogo = styled.div`
  width: 12px;
  height: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FitDropDown = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: absolute;
  z-index: 1000;
  top: 26px;
  left: 0;
  min-width: 100px;
  width: max-content;
  padding: var(--spacing-xs);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--background);
  box-shadow: var(--elevation-toast);
`;

const FitItem = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 90px;
  padding: 4px 8px;
  border-radius: var(--radius);
  border: none;
  transition: all 150ms ease-out;

  &:hover {
    background-color: var(--surface-elevated);

    ${SelectedFit} {
      color: var(--foreground);
    }
  }
`;

const FitItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  height: 12px;
`;

const FitRowWrapper = styled.div`
  position: relative;
`;

const SubDropDown = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: absolute;
  z-index: 1001;
  top: 0;
  left: 100%;
  margin-left: 4px;
  min-width: 120px;
  width: max-content;
  padding: var(--spacing-xs);
  border-radius: var(--radius);
  border: 1px solid var(--border);
  background: var(--background);
  box-shadow: var(--elevation-toast);
`;
